import moment, { Moment } from "moment-timezone";
import { TFunction } from "i18next";
import {
  AppointmentSummary,
  MemberSettings,
  WeekdayKey,
} from "../../../../hooks/agenda";

export type AgendaView = "day" | "week" | "month";

export const QUARTER_MINUTES = 15;
export const DEFAULT_START_HOUR = 9;
export const DEFAULT_END_HOUR = 20;
export const DEFAULT_DURATION_MINUTES = 30;

const LOCAL_INPUT_FORMAT = "YYYY-MM-DDTHH:mm";

const WEEKDAY_BY_MOMENT_DAY: WeekdayKey[] = [
  "sun",
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
];

export const browserTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || moment.tz.guess();

/** The time in another time zone, with its date when that is not the day the reader sees. */
export const otherZoneTime = (start: number, timezone: string): string => {
  const zoned = moment.tz(start, timezone);
  return zoned.format("YYYY-MM-DD") === moment(start).format("YYYY-MM-DD")
    ? zoned.format("LT")
    : zoned.format("lll");
};

export const nextFullHour = (): number =>
  moment().add(1, "hour").startOf("hour").valueOf();

export const wallMinutes = (ms: number): number => {
  const at = moment(ms);
  return at.hour() * 60 + at.minute();
};

// Adding hours to midnight drifts by one on the days the clocks change, and
// Moment's hour() setter lands an hour early inside a skipped hour. Built from
// its parts, a skipped time moves past the change, a repeated one is the first.
export const wallClockAt = (day: Moment, minutes: number): number =>
  moment({
    year: day.year(),
    month: day.month(),
    date: day.date(),
    hour: Math.floor(minutes / 60),
    minute: minutes % 60,
  }).valueOf();

export const dayBounds = (
  day: Moment,
  startHour: number,
  endHour: number
): { dayStart: number; dayEnd: number } => ({
  dayStart: wallClockAt(day, startHour * 60),
  dayEnd: wallClockAt(day, endHour * 60),
});

export const hourLabel = (hour: number): string =>
  moment.utc({ year: 2000, month: 0, date: 1, hour }).format("LT");

export interface ViewRange {
  days: Moment[];
  from: number;
  to: number;
}

export const viewRange = (view: AgendaView, anchor: Moment): ViewRange => {
  const start =
    view === "day"
      ? anchor.clone().startOf("day")
      : view === "week"
      ? anchor.clone().startOf("week")
      : anchor.clone().startOf("month").startOf("week");
  const count = view === "day" ? 1 : view === "week" ? 7 : 42;
  const days = Array.from({ length: count }, (_, i) =>
    start.clone().add(i, "day")
  );
  return {
    days,
    from: start.valueOf(),
    to: start.clone().add(count, "day").valueOf(),
  };
};

export const stepUnit = (view: AgendaView) =>
  view === "day" ? "day" : view === "week" ? "week" : "month";

/** Where another view opens from the period shown: today when it holds today, else its first day. */
export const anchorOnViewChange = (
  view: AgendaView,
  anchor: Moment,
  today: Moment = moment().startOf("day")
): Moment => {
  const unit = stepUnit(view);
  return today.isSame(anchor, unit)
    ? today.clone()
    : anchor.clone().startOf(unit);
};

export const capitalizeFirst = (value: string) =>
  value ? value.charAt(0).toLocaleUpperCase() + value.slice(1) : value;

export const dayLabel = (day: Moment): string =>
  capitalizeFirst(`${day.localeData().weekdays(day)} ${day.format("LL")}`);

// agenda.range.time exists in both the dashboard and the clientView namespaces, agenda.range.date and .week in the dashboard only.
export const rangeLabel = (
  t: TFunction,
  kind: "time" | "date",
  start: string,
  end: string
): string => t(`agenda.range.${kind}`, { start, end }) as string;

/** The end of an appointment, with its date when it runs past its first day. */
export const endLabel = ({ start, end }: { start: number; end: number }) => {
  const last = moment(end);
  return last.isSame(moment(start), "day")
    ? last.format("LT")
    : last.format("lll");
};

export const durationLabel = (
  t: TFunction,
  keyPrefix: string,
  minutes: number
): string => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return t(`${keyPrefix}.minutes`, { minutes }) as string;
  if (rest === 0)
    return t(`${keyPrefix}.hours`, { hours, count: hours }) as string;
  return t(`${keyPrefix}.hoursMinutes`, {
    hours,
    minutes: rest,
    count: hours,
  }) as string;
};

export const periodLabel = (
  view: AgendaView,
  anchor: Moment,
  t: TFunction
): string => {
  if (view === "day") return dayLabel(anchor);
  if (view === "week") {
    const start = anchor.clone().startOf("week");
    const end = start.clone().add(6, "day");
    return capitalizeFirst(
      t("agenda.range.week", {
        week: start.week(),
        range: rangeLabel(t, "date", start.format("ll"), end.format("ll")),
      }) as string
    );
  }
  return `${capitalizeFirst(
    moment.localeData().months(anchor)
  )} ${anchor.year()}`;
};

export const toLocalInput = (ms: number): string =>
  moment(ms).format(LOCAL_INPUT_FORMAT);

export const fromLocalInput = (value: string): number | null => {
  if (!value) return null;
  const parsed = moment(value, LOCAL_INPUT_FORMAT, true);
  return parsed.isValid() ? parsed.valueOf() : null;
};

export const weekdayKeyOf = (m: Moment): WeekdayKey =>
  WEEKDAY_BY_MOMENT_DAY[m.day()];

export const weekdayKeyIndex = (key: WeekdayKey): number =>
  WEEKDAY_BY_MOMENT_DAY.indexOf(key);

/** A range may end at midnight, written 24:00 as the server does; it never starts there. */
export const MIDNIGHT = "24:00";

/** Without availability, a person can be booked at any time; "HH:mm" stays below 24:00, so a range up to midnight holds. */
export const isWithinHours = (
  ms: number,
  settings: MemberSettings | null | undefined
): boolean => {
  if (!settings?.hours) return true;
  const local = moment.tz(ms, settings.tz || browserTimeZone());
  const ranges = settings.hours[weekdayKeyOf(local)] ?? [];
  const hhmm = local.format("HH:mm");
  return ranges.some(([start, end]) => hhmm >= start && hhmm < end);
};

interface Span {
  start: number;
  end: number;
}

const availabilitySpans = (
  settings: MemberSettings | null | undefined,
  from: number,
  to: number
): Span[] => {
  if (!settings?.hours) return [];
  const tz = settings.tz || browserTimeZone();
  const at = (day: Moment, hhmm: string) => {
    const [hour, minute] = hhmm.split(":").map(Number);
    return moment
      .tz(
        {
          year: day.year(),
          month: day.month(),
          date: day.date(),
          hour,
          minute,
        },
        tz
      )
      .valueOf();
  };
  const spans: Span[] = [];
  const day = moment.tz(from, tz).startOf("day");
  while (day.valueOf() < to) {
    (settings.hours[weekdayKeyOf(day)] ?? []).forEach(([start, end]) =>
      spans.push({ start: at(day, start), end: at(day, end) })
    );
    day.add(1, "day");
  }
  return spans;
};

/** `openingHour` is the first hour drawn, unless something crossing midnight draws the whole day. */
export const visibleHours = (
  appointments: AppointmentSummary[],
  days: Moment[],
  availability?: MemberSettings | null
): { startHour: number; endHour: number; openingHour: number } => {
  let startHour = DEFAULT_START_HOUR;
  let endHour = DEFAULT_END_HOUR;
  let openingHour = DEFAULT_START_HOUR;
  if (!days.length) return { startHour, endHour, openingHour };
  const rangeStart = days[0].valueOf();
  const rangeEnd = days[days.length - 1].clone().endOf("day").valueOf();
  const spans: Span[] = [
    ...appointments,
    ...availabilitySpans(availability, rangeStart, rangeEnd),
  ];
  spans.forEach((span) => {
    if (span.end <= rangeStart || span.start >= rangeEnd) return;
    const start = moment(Math.max(span.start, rangeStart));
    const lastInstant = moment(Math.min(span.end, rangeEnd + 1) - 1);
    if (start.isSame(lastInstant, "day")) {
      startHour = Math.min(startHour, start.hour());
      openingHour = Math.min(openingHour, start.hour());
      endHour = Math.max(endHour, lastInstant.hour() + 1);
    } else {
      startHour = 0;
      endHour = 24;
    }
  });
  return { startHour, endHour: Math.min(24, endHour), openingHour };
};

export interface PositionedAppointment {
  appointment: AppointmentSummary;
  start: number;
  end: number;
  column: number;
  columns: number;
}

export const layoutDay = (
  appointments: AppointmentSummary[],
  dayStart: number,
  dayEnd: number
): PositionedAppointment[] => {
  const items = appointments
    .filter((a) => a.end > dayStart && a.start < dayEnd)
    .map((a) => ({
      appointment: a,
      start: Math.max(a.start, dayStart),
      end: Math.max(Math.min(a.end, dayEnd), Math.max(a.start, dayStart) + 1),
      column: 0,
      columns: 1,
    }))
    .sort((a, b) => a.start - b.start || b.end - a.end);

  const placed: PositionedAppointment[] = [];
  let cluster: PositionedAppointment[] = [];
  let columnEnds: number[] = [];
  let clusterEnd = -Infinity;

  const closeCluster = () => {
    cluster.forEach((item) => {
      item.columns = columnEnds.length;
    });
    placed.push(...cluster);
    cluster = [];
    columnEnds = [];
  };

  items.forEach((item) => {
    if (item.start >= clusterEnd) closeCluster();
    let column = columnEnds.findIndex((end) => end <= item.start);
    if (column === -1) {
      column = columnEnds.length;
      columnEnds.push(item.end);
    } else {
      columnEnds[column] = item.end;
    }
    item.column = column;
    cluster.push(item);
    clusterEnd =
      cluster.length === 1 ? item.end : Math.max(clusterEnd, item.end);
  });
  closeCluster();
  return placed;
};

export const appointmentsOnDay = (
  appointments: AppointmentSummary[],
  day: Moment
): AppointmentSummary[] => {
  const start = day.clone().startOf("day").valueOf();
  const end = day.clone().endOf("day").valueOf();
  return appointments
    .filter((a) => a.end > start && a.start <= end)
    .sort((a, b) => a.start - b.start);
};

export const participantNames = (
  appointment: AppointmentSummary,
  exceptId?: string
): string =>
  appointment.participants
    .filter((p) => p.user_identification !== exceptId)
    .map((p) => p.name)
    .filter(Boolean)
    .join(", ");
